import { TextInput, WarningTriangleIcon } from '@cfs-fixings/shared'
import classNames from 'classnames'
import { InputHTMLAttributes, forwardRef } from 'react'

interface PanelInputProps extends InputHTMLAttributes<HTMLInputElement> {
  defaultValue?: InputHTMLAttributes<HTMLInputElement>['defaultValue']
  value?: InputHTMLAttributes<HTMLInputElement>['value']
  preventKeys?: string[]
  title: string
  error?: Boolean
  onBlur?: InputHTMLAttributes<HTMLInputElement>['onBlur']
  onChange?: InputHTMLAttributes<HTMLInputElement>['onChange']
  name?: InputHTMLAttributes<HTMLInputElement>['name']
  units?: string
  inputSize?: 'sm' | 'md'
  titleDirection?: 'row' | 'col'
}

const PanelInput = forwardRef<HTMLInputElement, PanelInputProps>(function PanelInput ({ defaultValue, title, error, preventKeys, onBlur, onChange, value, name, units, inputSize = 'md', titleDirection = 'row', ...rest }, ref) {
  return (
    <div className={classNames('flex w-full',
      {
        'justify-between items-center': titleDirection === 'row',
        'flex-col gap-y-2': titleDirection === 'col',
      })
    }
    >
      <h3
        className={classNames(
          'flex items-center font-medium text-xs leading-[12.6px]',
          {
            'text-error-800': error,
            'text-gray-650': !error,
          },
        )}
      >
        <span>
          {parseTitle(title)}
        </span>
        {
          error
          && <WarningTriangleIcon className="h-[16px] w-[16px] text-error-500 ml-[7px]" />
        }
      </h3>
      <div className={classNames('',
        {
          'max-w-[54px] w-full': inputSize === 'sm',
          'w-[100px]': inputSize === 'md',
        },
      )}
      >
        <TextInput
          ref={ref}
          hasError={Boolean(error)}
          variant="xs"
          min={0}
          step={1}
          type="number"
          defaultValue={defaultValue}
          onKeyDown={e => {
            if ((preventKeys ?? []).includes(e.code)) {
              e.preventDefault()
            } else if (e.key === 'Enter' && onBlur) {
              (e.target as HTMLElement).blur()
            }
          }}
          onBlur={onBlur}
          onChange={onChange}
          value={value}
          name={name}
          postfix={units}
          {...rest}
        />
      </div>
    </div>
  )
})

export default PanelInput

/**
 * Function is used to interpolate small tags in the title for equation titles
 */
const parseTitle = (text: string) => {
  const parts = []
  let lastIndex = 0
  const regex = /\[small\](.*?)\[\/small\]/g
  let match

  // Find all matches for [small]...[/small] and add them to the parts array
  while ((match = regex.exec(text)) !== null) {
    // Push any text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    // Push the matched text wrapped in <small>
    parts.push(<small key={match.index} className="text-[8px]">{match[1]}</small>)
    lastIndex = regex.lastIndex
  }

  // Add any remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts
}
